import { api } from '../../shared/api/client'
import type {
  AgentClass,
  AgentOwnerVerdict,
  AgentRun,
  AgentState,
  MyReport,
  WorkItem,
  WorkItemCounters,
  WorkItemDetail,
  WorkItemListResponse,
  WorkItemPriority,
  WorkItemResolution,
  WorkItemSeverity,
  WorkItemStatus,
  WorkItemType,
} from './types'

export interface WorkItemFilters {
  page?: number
  limit?: number
  search?: string
  type?: WorkItemType | ''
  status?: WorkItemStatus | ''
  priority?: WorkItemPriority | ''
  severity?: WorkItemSeverity | ''
  assigneeId?: number | 'unassigned' | ''
  reporterId?: number
  openOnly?: boolean
  /** Классы агента через запятую (контракт §2.1). */
  agentClass?: AgentClass | string | ''
  /** Состояния агента через запятую. */
  agentState?: AgentState | string | ''
  sort?: string
  direction?: 'asc' | 'desc'
}

/** Код ошибки ответа гейтвея (`error.code`), например AGENT_RUN_ACTIVE. */
export function apiErrorCode(error: any): string | undefined {
  const code = error?.response?.data?.error?.code
  return typeof code === 'string' ? code : undefined
}
export const workItemsAPI = {
  createReport: (payload: {
    title: string
    details?: string
    sourcePageUrl: string
    sourcePageTitle: string
    clientContext: Record<string, unknown>
    idempotencyKey: string
  }) => api.post<{ success: true; data: WorkItem & { report_id: number } }>('/work-item-reports', payload),

  uploadAttachments: (itemId: number, reportId: number | undefined, files: File[]) => {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    if (reportId) formData.append('reportId', String(reportId))
    return api.post(`/work-items/${itemId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })
  },

  list: (filters: WorkItemFilters) =>
    api.get<{ success: true; data: WorkItemListResponse }>('/work-items', {
      params: Object.fromEntries(
        Object.entries(filters)
          .filter(([, value]) => value !== '' && value !== undefined)
          .map(([key, value]) => [key, typeof value === 'boolean' ? String(value) : value]),
      ),
    }),

  counters: () =>
    api.get<{ success: true; data: WorkItemCounters }>('/work-items/counters'),

  get: (id: number) =>
    api.get<{ success: true; data: WorkItemDetail }>(`/work-items/${id}`),

  create: (payload: {
    title: string
    description?: string
    type: WorkItemType
    priority: WorkItemPriority
    severity?: WorkItemSeverity | ''
    assigneeId?: number | null
  }) => api.post<{ success: true; data: WorkItem }>('/work-items', payload),

  update: (id: number, payload: Record<string, unknown>) =>
    api.patch<{ success: true; data: WorkItem }>(`/work-items/${id}`, payload),

  transition: (
    id: number,
    status: WorkItemStatus,
    resolution?: WorkItemResolution,
  ) => api.post<{ success: true; data: WorkItem }>(`/work-items/${id}/transitions`, {
    status,
    resolution,
  }),

  addComment: (id: number, body: string) =>
    api.post(`/work-items/${id}/comments`, { body }),

  merge: (id: number, targetId: number) =>
    api.post(`/work-items/${id}/merge`, { targetId }),

  // ─── ИИ-агент беклога (контракт §2.2) ───

  /** «Передать агенту»: ставит триаж; 409 AGENT_RUN_ACTIVE / TICKET_AGENT_DISABLED. */
  enqueueAgentRun: (id: number, payload: { kind: 'triage' } = { kind: 'triage' }) =>
    api.post<{ success: true; data: AgentRun }>(`/work-items/${id}/agent-runs`, payload),

  listAgentRuns: (id: number) =>
    api.get<{ success: true; data: AgentRun[] }>(`/work-items/${id}/agent-runs`),

  /** Оценка разбора владельцем: «agree» или верный класс. */
  setAgentOwnerVerdict: (runId: number, verdict: AgentOwnerVerdict) =>
    api.patch<{ success: true; data: AgentRun }>(`/work-items/agent-runs/${runId}/verdict`, { verdict }),

  // ─── Действия автора над своим обращением: /work-item-reports/items/:id/* (Backlog:READ_OWN) ───

  getMyReports: (limit = 50) =>
    api.get<{ success: true; data: { items: MyReport[] } }>('/work-item-reports/mine', { params: { limit } }),

  addReporterComment: (id: number, body: string) =>
    api.post<{ success: true; data: { id: number; created_at: string | null } }>(
      `/work-item-reports/items/${id}/comments`,
      { body },
    ),

  confirmFix: (id: number) =>
    api.post<{ success: true; data: WorkItem }>(`/work-item-reports/items/${id}/confirm-fix`),

  rejectFix: (id: number, comment: string) =>
    api.post<{ success: true; data: WorkItem }>(`/work-item-reports/items/${id}/reject-fix`, { comment }),

  downloadAttachment: async (attachmentId: number, fileName: string) => {
    const response = await api.get(`/work-items/attachments/${attachmentId}`, {
      responseType: 'blob',
    })
    const url = URL.createObjectURL(response.data)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  },
}
